import { LayerThematicStylesDto } from '@cloud-twin-v2/generated/simulation.generated.client';
import { Leaflet } from '@common-modules/wlm-map/leaflet-export';

const getThematicId = (objectType: string, styleId: string) =>
  objectType + styleId ? `#${styleId}` : '';

const getThematicIdFromStyle = (style: LayerThematicStylesDto) =>
  style ? getThematicId(style.objectType, style.id) : null;

/**
 * Analyzes a Leaflet map and returns a summary of all panes,
 * their order by zIndex and the layers associated with each pane
 *
 * @param {Leaflet.Map} map - The Leaflet map to analyze
 * @returns {Object} Complete summary of panes and layers
 */
export function analyzePanesAndLayers(map) {
  if (!map || !map.getContainer) {
    throw new Error('A valid Leaflet map is required');
  }

  const result = {
    totalPanes: 0,
    totalLayers: 0,
    panes: [],
    summary: {
      panesWithLayers: 0,
      panesWithoutLayers: 0,
      layersWithZIndex: 0,
      layersWithoutZIndex: 0,
    },
  };

  // Get all panes from the map
  const panes = map.getPanes();
  const panesArray = [];

  // Convert panes to array with additional information
  for (const [paneName, paneElement] of Object.entries(panes)) {
    const paneInfo = {
      name: paneName,
      element: paneElement,
      zIndex: getZIndex(paneElement),
      layers: [],
      layerCount: 0,
    };

    // Search for layers associated with this pane
    map.eachLayer((layer) => {
      if (layer.getPane && layer.getPane() === paneElement) {
        const layerInfo = {
          type: getLayerType(layer),
          zIndex: getLayerZIndex(layer),
          id: layer._leaflet_id || 'No ID',
          options: extractRelevantOptions(layer.options || {}),
        };
        paneInfo.layers.push(layerInfo);
      }
    });

    paneInfo.layerCount = paneInfo.layers.length;
    panesArray.push(paneInfo);
  }

  // Sort panes by zIndex (those without zIndex go to the end)
  panesArray.sort((a, b) => {
    if (a.zIndex === null && b.zIndex === null) return 0;
    if (a.zIndex === null) return 1;
    if (b.zIndex === null) return -1;
    return a.zIndex - b.zIndex;
  });

  // Sort layers within each pane by zIndex
  panesArray.forEach((pane) => {
    pane.layers.sort((a, b) => {
      if (a.zIndex === null && b.zIndex === null) return 0;
      if (a.zIndex === null) return 1;
      if (b.zIndex === null) return -1;
      return a.zIndex - b.zIndex;
    });
  });

  // Calculate statistics
  result.totalPanes = panesArray.length;
  result.totalLayers = panesArray.reduce((sum, pane) => sum + pane.layerCount, 0);
  result.summary.panesWithLayers = panesArray.filter((pane) => pane.layerCount > 0).length;
  result.summary.panesWithoutLayers = panesArray.filter((pane) => pane.layerCount === 0).length;

  panesArray.forEach((pane) => {
    pane.layers.forEach((layer) => {
      if (layer.zIndex !== null) {
        result.summary.layersWithZIndex++;
      } else {
        result.summary.layersWithoutZIndex++;
      }
    });
  });

  result.panes = panesArray;

  return result;
}

/**
 * Gets the zIndex of a DOM element
 */
function getZIndex(element) {
  if (!element || !element.style) return null;

  const zIndex = element.style.zIndex || window.getComputedStyle(element).zIndex;

  return zIndex && zIndex !== 'auto' ? parseInt(zIndex, 10) : null;
}

/**
 * Gets the zIndex of a Leaflet layer if available
 */
function getLayerZIndex(layer) {
  // Check different ways layers can have zIndex
  if (layer.options && layer.options.zIndex !== undefined) {
    return layer.options.zIndex;
  }

  if (layer.setZIndex && layer.getZIndex) {
    return layer.getZIndex();
  }

  if (layer._container) {
    return getZIndex(layer._container);
  }

  return null;
}

/**
 * Determines the type of Leaflet layer
 */
function getLayerType(layer) {
  if (layer instanceof Leaflet.TileLayer) return 'TileLayer';
  if (layer instanceof Leaflet.ImageOverlay) return 'ImageOverlay';
  if (layer instanceof Leaflet.VideoOverlay) return 'VideoOverlay';
  if (layer instanceof Leaflet.SVGOverlay) return 'SVGOverlay';
  if (layer instanceof Leaflet.Marker) return 'Marker';
  if (layer instanceof Leaflet.Popup) return 'Popup';
  if (layer instanceof Leaflet.Tooltip) return 'Tooltip';
  if (layer instanceof Leaflet.Path) {
    if (layer instanceof Leaflet.Polygon) return 'Polygon';
    if (layer instanceof Leaflet.Polyline) return 'Polyline';
    if (layer instanceof Leaflet.Circle) return 'Circle';
    if (layer instanceof Leaflet.CircleMarker) return 'CircleMarker';
    if (layer instanceof Leaflet.Rectangle) return 'Rectangle';
    return 'Path';
  }
  if (layer instanceof Leaflet.LayerGroup) return 'LayerGroup';
  if (layer instanceof Leaflet.FeatureGroup) return 'FeatureGroup';
  if (layer instanceof Leaflet.GeoJSON) return 'GeoJSON';

  return layer?.constructor.name || 'Unknown';
}

/**
 * Extracts relevant layer options for the summary
 */
function extractRelevantOptions(options) {
  const relevant = {};
  const importantProperties = [
    'opacity',
    'zIndex',
    'interactive',
    'attribution',
    'pane',
    'className',
    'weight',
    'color',
    'fillColor',
  ];

  importantProperties.forEach((prop) => {
    if (options[prop] !== undefined) {
      relevant[prop] = options[prop];
    }
  });

  return relevant;
}

/**
 * Helper function to display the summary in a readable format
 */
export function displayMapSummary(summary) {
  console.log('=== PANES AND LAYERS SUMMARY ===');
  console.log(`Total panes: ${summary.totalPanes}`);
  console.log(`Total layers: ${summary.totalLayers}`);
  console.log(`Panes with layers: ${summary.summary.panesWithLayers}`);
  console.log(`Panes without layers: ${summary.summary.panesWithoutLayers}`);
  console.log(`Layers with zIndex: ${summary.summary.layersWithZIndex}`);
  console.log(`Layers without zIndex: ${summary.summary.layersWithoutZIndex}`);
  console.log('\n=== DETAILS BY PANES (ordered by zIndex) ===');

  summary.panes.forEach((pane, index) => {
    console.log(`\n${index + 1}. Pane: "${pane.name}"`);
    console.log(`   zIndex: ${pane.zIndex !== null ? pane.zIndex : 'Not defined'}`);
    console.log(`   Layer count: ${pane.layerCount}`);

    if (pane.layers.length > 0) {
      console.log('   Layers (ordered by zIndex):');
      pane.layers.forEach((layer, layerIndex) => {
        console.log(`     ${layerIndex + 1}. ${layer.type} (ID: ${layer.id})`);
        console.log(`        zIndex: ${layer.zIndex !== null ? layer.zIndex : 'Not defined'}`);
        if (Object.keys(layer.options).length > 0) {
          console.log(`        Options: ${JSON.stringify(layer.options)}`);
        }
      });
    }
  });
}

// Usage example:
// const summary = analyzePanesAndLayers(myMap);
// displaySummary(summary);
//
// // Or access data directly:
// console.log(summary.panes[0]); // First pane
// console.log(summary.totalLayers); // Total layers

function debugMapTooltips(map) {
  const results = {
    domTooltips: [],
    layerTooltips: [],
    emptyTooltips: [],
  };

  // Check DOM tooltips
  const tooltipPane = map.getPane('tooltipPane');
  const domTooltips = tooltipPane.querySelectorAll('.leaflet-tooltip');

  domTooltips.forEach((tooltip) => {
    const isEmpty = tooltip.innerHTML.trim() === '';
    const tooltipData = {
      element: tooltip,
      content: tooltip.innerHTML,
      isEmpty: isEmpty,
      classes: tooltip.className,
    };

    results.domTooltips.push(tooltipData);
    if (isEmpty) results.emptyTooltips.push(tooltipData);
  });

  // Check layers with tooltips
  map.eachLayer((layer) => {
    if (layer.getTooltip && layer.getTooltip()) {
      const tooltip = layer.getTooltip();
      const tooltipData = {
        layer: layer,
        tooltip: tooltip,
        content: tooltip.getContent(),
        isEmpty: !tooltip.getContent() || tooltip.getContent().toString().trim() === '',
        isOpen: tooltip._map ? true : false,
      };

      results.layerTooltips.push(tooltipData);
      if (tooltipData.isEmpty && tooltipData.isOpen) {
        results.emptyTooltips.push(tooltipData);
      }
    }
  });

  console.log('Tooltip Debug Results:', results);

  if (results.emptyTooltips.length > 0) {
    console.warn(`Found ${results.emptyTooltips.length} empty tooltip(s)!`);
  }

  return results;
}

function getCurrentMapState(map) {
  if (!map) {
    return;
  }

  // Get all panes
  const panes = Object.keys(map._panes);

  // Get all layers
  const layers = [];
  map.eachLayer(function (layer) {
    layers.push(layer);
  });

  return {
    panes,
    layers,
  };
}

export const genericMapUtils = {
  analyzePanesAndLayers,
  debugMapTooltips,
  getCurrentMapState,
  getLayerType,
  getThematicIdFromStyle,
  getThematicId,
};
